import { useState } from 'react'

interface Props {
  apiBase: string
  downloadUrl: string
}

// Standalone "create a test account" flow for the viewer's landing page
// (shown when someone opens the viewer without a /share/<token>). Unlike
// AddToWorkspace, this isn't tied to a share — it just lets a tester create an
// account against the signal server, then points them at the desktop download
// so they can sign in and use the system themselves.
//
// Same auth contract as the rest of the system:
//   POST /accounts/signup { email, password, handle? } -> { ok, sessionToken }
//   POST /accounts/login  { email, password }          -> { ok, sessionToken }
// 409 on signup means the email already exists; we flip to login.

type Mode = 'signup' | 'login'

export default function TestAccountSignup({ apiBase, downloadUrl }: Props): JSX.Element {
  const [mode, setMode] = useState<Mode>('signup')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [handle, setHandle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [doneEmail, setDoneEmail] = useState<string | null>(null)

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    const path = mode === 'signup' ? '/accounts/signup' : '/accounts/login'
    const body =
      mode === 'signup'
        ? { email: email.trim().toLowerCase(), password, handle: handle.trim() || null }
        : { email: email.trim().toLowerCase(), password }
    try {
      const res = await fetch(`${apiBase}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      const j = (await res.json().catch(() => ({}))) as { sessionToken?: string; error?: string }
      if (!res.ok) {
        if (res.status === 409) {
          setError('That email already has an account — log in instead.')
          setMode('login')
        } else {
          setError(j.error || `${mode === 'signup' ? 'Signup' : 'Login'} failed (${res.status}).`)
        }
        return
      }
      if (!j.sessionToken) {
        setError('The server did not return a session. Try again.')
        return
      }
      setDoneEmail(body.email)
    } catch (err) {
      setError(`Could not reach the server: ${(err as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  if (doneEmail) {
    return (
      <div className="glass-card rounded-2xl p-6" data-testid="signup-success">
        <div className="flex items-start gap-3">
          <div
            className="h-10 w-10 rounded-xl inline-flex items-center justify-center text-[18px] shrink-0"
            style={{
              background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.25), rgba(52, 211, 153, 0.10))',
              color: 'rgb(167, 243, 208)'
            }}
          >
            ✓
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-stone-100 mb-1">Your test account is ready</h3>
            <p className="text-[13px] text-stone-300 leading-relaxed mb-4">
              Download Plexii, then sign in with{' '}
              <span className="font-mono text-stone-200">{doneEmail}</span> and the password you just
              set. Everything you create stays on your machine and syncs through this test server.
            </p>
            <a href={downloadUrl} className="btn-primary inline-block" target="_blank" rel="noopener noreferrer">
              Download Plexii
            </a>
          </div>
        </div>
      </div>
    )
  }

  const isLogin = mode === 'login'
  return (
    <div className="glass-card rounded-2xl p-6" data-testid="signup-card">
      <h3 className="text-base font-semibold text-stone-100 mb-1">
        {isLogin ? 'Log in to your test account' : 'Create a test account'}
      </h3>
      <p className="text-[12px] text-stone-400 mb-4">
        {isLogin
          ? 'Sign in to confirm your account, then download the app.'
          : 'Make an account on the test server, then download Plexii and sign in to use it.'}
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            data-testid="signup-email"
            className="w-full px-3 py-2 rounded-md text-stone-100 placeholder:text-stone-500"
            style={{ background: 'rgba(0,0,0,0.32)', border: '1px solid rgba(255,255,255,0.08)' }}
            placeholder="you@example.com"
          />
        </div>
        {!isLogin && (
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">
              Display handle{' '}
              <span className="text-stone-500 normal-case font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="what your shares will say"
              maxLength={32}
              data-testid="signup-handle"
              className="w-full px-3 py-2 rounded-md text-stone-100 placeholder:text-stone-500"
              style={{ background: 'rgba(0,0,0,0.32)', border: '1px solid rgba(255,255,255,0.08)' }}
            />
          </div>
        )}
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            data-testid="signup-password"
            className="w-full px-3 py-2 rounded-md text-stone-100 placeholder:text-stone-500"
            style={{ background: 'rgba(0,0,0,0.32)', border: '1px solid rgba(255,255,255,0.08)' }}
            placeholder={isLogin ? 'your password' : 'at least 8 characters'}
          />
        </div>
        {error && (
          <div
            className="text-[12px] px-3 py-2 rounded-md"
            data-testid="signup-error"
            style={{
              background: 'rgba(244,114,182,0.10)',
              border: '1px solid rgba(244,114,182,0.25)',
              color: 'rgb(251, 207, 232)'
            }}
          >
            {error}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            type="button"
            onClick={() => {
              setMode(isLogin ? 'signup' : 'login')
              setError(null)
            }}
            className="text-[12px] text-stone-400 hover:text-stone-100 transition-colors"
          >
            {isLogin ? 'Need an account? Sign up' : 'Already have one? Log in'}
          </button>
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="signup-submit">
            {busy ? 'Working…' : isLogin ? 'Log in' : 'Create account'}
          </button>
        </div>
      </form>
    </div>
  )
}
